/**
 * Progressive enhancement: Select2 dropdowns + intl-tel-input phone field.
 *
 * Select2 must be initialised on the FIRST SHOW of each panel — it measures 0
 * width inside a display:none container (the classic trap). init-on-show +
 * width:'100%' avoids it. Idempotent via a Set of already-initialised steps.
 */

import type { IewConfig, ItiInstance } from './types';

// Select2 is a jQuery plugin registered by the vendored script. We declare the
// minimal surface rather than pull in @types/select2 (not installed).
interface Select2Options {
  tags?: boolean;
  maximumSelectionLength?: number;
  width?: string;
  dropdownParent?: JQuery;
  placeholder?: string;
  language?: { noResults?: () => string };
}
interface Select2JQuery extends JQuery<HTMLElement> {
  select2(options?: Select2Options): Select2JQuery;
}

export class Enhancer {
  private readonly initialised = new Set<number>();
  private iti: ItiInstance | null = null;
  private itiInput: HTMLInputElement | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly config: IewConfig,
    private readonly $: JQueryStatic,
  ) {}

  /** Idempotent per-step enhancement — call on first show of a panel. */
  initStep(step: number): void {
    if (this.initialised.has(step)) {
      return;
    }
    this.initialised.add(step);

    const panel = this.root.querySelector<HTMLElement>(`.iew-step[data-step="${step}"]`);
    if (!panel) {
      return;
    }

    panel.querySelectorAll<HTMLSelectElement>('select[data-iew-select2]').forEach((select) => {
      const $select = this.$(select) as unknown as Select2JQuery;
      const tags = select.dataset.tags === '1';
      const max = select.dataset.max ? Number(select.dataset.max) : undefined;
      const placeholder = select.dataset.placeholder;
      // Tags fields start with no options, so an untouched dropdown would show
      // "No results found". Prompt the user to type a value to add instead.
      const tagsHint = tags ? this.config.i18n.tagsHint : undefined;
      // No dropdownParent: the panel attaches to <body> so Select2's native,
      // viewport-aware up/down placement works. Parenting it to the padded card
      // made it mis-measure available space and flip "above", overlapping the
      // field label. Our .select2-dropdown skin is global, so it styles the
      // body-level panel just the same.
      $select.select2({
        tags,
        maximumSelectionLength: max,
        width: '100%',
        placeholder,
        language: tagsHint ? { noResults: () => tagsHint } : undefined,
      });
    });

    const phone = panel.querySelector<HTMLInputElement>('input.iew-phone');
    if (phone && window.intlTelInput) {
      this.itiInput = phone;
      this.iti = window.intlTelInput(phone, {
        initialCountry: 'md',
        separateDialCode: true,
        // webpackIgnore keeps webpack from trying to bundle the runtime import
        // of the vendored utils.js URL.
        loadUtils: () => import(/* webpackIgnore: true */ this.config.itiUtilsUrl),
      });
    }
  }

  /**
   * Current phone number in E.164. getNumber() *throws*
   * ("intlTelInput.utils is required for getNumber()") until intl-tel-input's
   * utils.js has finished loading (async via loadUtils) — which can happen while
   * a draft restore is already collecting values. We swallow that and fall back
   * to reconstructing the number from the visible dial code (rendered by
   * separateDialCode) + the raw national input, keeping collection correct even
   * before utils resolves.
   */
  phoneNumber(): string {
    if (this.iti) {
      try {
        const n = this.iti.getNumber();
        if (n) {
          return n;
        }
      } catch {
        // utils not loaded yet — fall through to the raw-input reconstruction.
      }
    }
    const raw = this.itiInput ? this.itiInput.value.trim() : '';
    if (raw === '') {
      return '';
    }
    if (raw.startsWith('+')) {
      return raw;
    }
    const dial = this.dialCode();
    const national = raw.replace(/[^\d]/g, '');
    return dial !== '' ? `${dial}${national}` : raw;
  }

  /** Read the visible dial code that separateDialCode renders next to the input. */
  private dialCode(): string {
    if (!this.itiInput) {
      return '';
    }
    const container = this.itiInput.closest('.iti');
    const el = container?.querySelector<HTMLElement>('.iti__selected-dial-code');
    const text = el?.textContent?.trim() ?? '';
    if (text === '') {
      return '';
    }
    return text.startsWith('+') ? text : `+${text}`;
  }

  /** Restore a phone value into the ITI widget (used by draft restore). */
  setPhone(value: string): void {
    if (this.iti && value) {
      this.iti.setNumber(value);
    } else if (this.itiInput) {
      this.itiInput.value = value;
    }
  }

  /**
   * Add an <option> to a Select2 tags field and select it (draft restore needs
   * the option to exist before the value can be set).
   */
  addTagOption(select: HTMLSelectElement, value: string): void {
    if (!Array.from(select.options).some((o) => o.value === value)) {
      const opt = new Option(value, value, true, true);
      select.add(opt);
    }
  }
}
